import { $$, isMobile } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

const SELECTOR = '[data-au-input-predictive]';
const INITIALIZED_FLAG = 'auPredictiveInitialized';
const HIDDEN_CLASS = 'au-visually-hidden';
const ACTIVE_CLASS = 'au-active';
const CLEAR_ACTIVE_CLASS = 'au-float-input--clear-active';
const OPTION_HIDDEN_ATTR = 'data-au-option-hidden';

function init() {
  initInputs();
  addEventListeners();
}

function initInputs(context = document) {
  const inputs = $$(SELECTOR, context);

  inputs.forEach((input) => {
    if (input.dataset[INITIALIZED_FLAG]) return;
    setupPredictiveInput(input);
    input.dataset[INITIALIZED_FLAG] = 'true';
  });
}

// --- Helpers propios para el botón clear ---

function predictiveInputContainerAddActive(inputContainer) {
  inputContainer.classList.add(CLEAR_ACTIVE_CLASS);
}

function predictiveInputContainerRemoveActive(inputContainer) {
  inputContainer.classList.remove(CLEAR_ACTIVE_CLASS);
}

function setupPredictiveInput(input) {
  const mainWrapper = input.closest('[data-au-input-predictive-main]');
  if (!mainWrapper) return;

  const listboxId =
    input.getAttribute('aria-controls') || mainWrapper.getAttribute('aria-controls') || `${input.id}-listbox`;
  const listbox = listboxId ? document.getElementById(listboxId) : null;

  if (!listbox) return;

  let activeOption = null;
  let isClearingFromButton = false;
  let suppressNextFocusOpen = false;
  let suppressFocusResetTimer = null;

  const liveRegion = createLiveRegion();

  const inputContainer = input.closest('[data-au-float-input]');
  const clearButton = inputContainer?.querySelector('[data-au-float-input-clear]');
  const INPUT_TOOLTIP_ATTR = 'data-au-input-predictive-tooltip';
  const OPTION_TOOLTIP_ATTR = 'data-au-input-predictive-listbox-tooltip';
  const isMobileDevice = typeof isMobile === 'object' && isMobile !== null ? isMobile.matches : Boolean(isMobile);

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-haspopup', 'listbox');
  input.setAttribute('aria-controls', listbox.id);
  input.setAttribute('aria-activedescendant', '');
  input.setAttribute('aria-autocomplete', 'list');

  const getOptions = () => Array.from(listbox.querySelectorAll('[data-au-listbox-option]'));

  const getVisibleOptions = () => getOptions().filter((opt) => !opt.hasAttribute(OPTION_HIDDEN_ATTR));

  const getOptionText = (option) => {
    const textEl = option.querySelector('[data-au-input-predictive-listbox-text]');
    return textEl ? textEl.textContent.trim() : '';
  };

  const isOptionDisabled = (option) => option.hasAttribute('data-au-listbox-option-disabled');

  const updateClearButtonVisibility = () => {
    if (!inputContainer) return;

    if (input.value.trim().length > 0) {
      predictiveInputContainerAddActive(inputContainer);
    } else {
      predictiveInputContainerRemoveActive(inputContainer);
    }
  };

  const removeSelectedValueTooltip = () => {
    const tooltip = mainWrapper.querySelector(`[${INPUT_TOOLTIP_ATTR}]`);
    if (tooltip) {
      tooltip.remove();
    }

    input.removeAttribute('title');
  };

  const updateSelectedValueTooltip = () => {
    removeSelectedValueTooltip();

    if (isMobileDevice || !input.value.trim()) return;

    const computedStyles = window.getComputedStyle(input);
    const measureCanvas = document.createElement('canvas');
    const measureContext = measureCanvas.getContext('2d');

    if (!measureContext) return;

    measureContext.font = computedStyles.font;

    const letterSpacing = Number.parseFloat(computedStyles.letterSpacing);
    const letterSpacingWidth = Number.isNaN(letterSpacing) ? 0 : Math.max(input.value.length - 1, 0) * letterSpacing;

    const textWidth = measureContext.measureText(input.value).width + letterSpacingWidth;

    if (textWidth <= input.clientWidth) return;

    input.setAttribute('title', input.value);

    const tooltip = document.createElement('span');
    tooltip.setAttribute('aria-hidden', 'true');
    tooltip.setAttribute(INPUT_TOOLTIP_ATTR, '');
    tooltip.classList.add('au-input-predictive__tooltip');
    tooltip.textContent = input.value;

    mainWrapper.appendChild(tooltip);
  };

  const removeOptionTooltip = (option) => {
    const tooltip = option.querySelector(`[${OPTION_TOOLTIP_ATTR}]`);
    if (tooltip) {
      tooltip.remove();
    }

    const textEl = option.querySelector('[data-au-input-predictive-listbox-text]');
    if (textEl) {
      textEl.removeAttribute('title');
    }
  };

  const removeAllOptionTooltips = () => {
    getOptions().forEach((option) => {
      removeOptionTooltip(option);
    });
  };

  const addOptionTooltip = (option) => {
    const textEl = option.querySelector('[data-au-input-predictive-listbox-text]');
    if (!textEl) return;

    removeOptionTooltip(option);

    if (isMobileDevice) return;

    const textOverflow = textEl.scrollWidth > textEl.clientWidth;
    const optionOverflow = option.scrollWidth > option.clientWidth;
    if (!textOverflow && !optionOverflow) return;

    textEl.setAttribute('title', textEl.innerText);

    const tooltip = document.createElement('span');
    tooltip.setAttribute('aria-hidden', 'true');
    tooltip.setAttribute(OPTION_TOOLTIP_ATTR, '');
    tooltip.classList.add('au-input-predictive__listbox-tooltip');
    tooltip.textContent = textEl.innerText;

    option.appendChild(tooltip);

    if (option.dataset.auPredictiveTooltipBound !== 'true') {
      option.addEventListener('mousemove', (e) => {
        const optionTooltip = option.querySelector(`[${OPTION_TOOLTIP_ATTR}]`);
        if (!optionTooltip) return;

        optionTooltip.style.left = `${e.clientX - optionTooltip.offsetWidth / 2}px`;
        optionTooltip.style.top = `${e.clientY - 56}px`;
        optionTooltip.style.visibility = 'visible';
        optionTooltip.style.opacity = 1;
      });

      option.addEventListener('mouseleave', () => {
        const optionTooltip = option.querySelector(`[${OPTION_TOOLTIP_ATTR}]`);
        if (!optionTooltip) return;

        optionTooltip.style.visibility = 'hidden';
        optionTooltip.style.opacity = 0;
      });

      option.dataset.auPredictiveTooltipBound = 'true';
    }
  };

  const updateOptionTooltips = () => {
    getOptions().forEach((option) => {
      if (option.hasAttribute(OPTION_HIDDEN_ATTR)) {
        removeOptionTooltip(option);
        return;
      }

      addOptionTooltip(option);
    });
  };

  const suppressFocusOpenOnce = () => {
    suppressNextFocusOpen = true;

    if (suppressFocusResetTimer) {
      clearTimeout(suppressFocusResetTimer);
    }

    suppressFocusResetTimer = setTimeout(() => {
      suppressNextFocusOpen = false;
      suppressFocusResetTimer = null;
    }, 0);
  };

  const openList = () => {
    listbox.classList.remove(HIDDEN_CLASS);
    input.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      updateOptionTooltips();
    });
  };

  const closeList = () => {
    listbox.classList.add(HIDDEN_CLASS);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-activedescendant', '');
    removeAllOptionTooltips();
    clearActiveOption();
  };

  const clearActiveOption = () => {
    if (activeOption) {
      activeOption.classList.remove(ACTIVE_CLASS);
    }
    activeOption = null;
  };

  const setActiveOption = (option) => {
    clearActiveOption();
    if (!option) return;

    activeOption = option;
    activeOption.classList.add(ACTIVE_CLASS);
    input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  };

  const pushSelectionTracking = (selectedValue) => {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;

    const formElement = input.closest('[data-au-form-element]');
    const labelText = formElement?.querySelector('label')?.innerText?.trim();
    if (!labelText || !selectedValue) return;

    const predictiveLabel = removeAccentMarkAndUppercase(labelText);
    const predictiveValue = removeAccentMarkAndUppercase(selectedValue.split(', ').join('_'));

    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickModulo',
      item_id: `${predictiveLabel}_${predictiveValue}`,
    });
  };

  const selectOption = (option) => {
    const value = getOptionText(option);

    getOptions().forEach((opt) => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    input.value = value;
    closeList();

    updateClearButtonVisibility();
    updateSelectedValueTooltip();
    pushSelectionTracking(value);

    input.dispatchEvent(new Event('change', { bubbles: true }));

    input.focus();
  };

  const filterOptions = (filterText = '') => {
    const normalizedFilter = removeAccentMarkAndUppercase(filterText);
    let visibleCount = 0;

    getOptions().forEach((option) => {
      const optionText = getOptionText(option);
      const normalizedText = removeAccentMarkAndUppercase(optionText);
      const matches = normalizedText.includes(normalizedFilter);

      if (matches) {
        option.removeAttribute(OPTION_HIDDEN_ATTR);
        option.style.display = '';
        visibleCount += 1;
      } else {
        option.setAttribute(OPTION_HIDDEN_ATTR, 'true');
        option.style.display = 'none';
      }
    });

    announceResults(visibleCount, liveRegion);

    if (visibleCount > 0) {
      openList();
    } else {
      closeList();
    }
  };

  // --- EVENTOS ---

  input.addEventListener('focus', () => {
    if (suppressNextFocusOpen) {
      suppressNextFocusOpen = false;
      return;
    }

    filterOptions(input.value);
  });

  input.addEventListener('click', () => {
    if (!listbox.classList.contains(HIDDEN_CLASS)) return;
    filterOptions(input.value);
  });

  input.addEventListener('input', () => {
    if (isClearingFromButton) {
      isClearingFromButton = false;
      closeList();
      updateClearButtonVisibility();
      removeSelectedValueTooltip();
      return;
    }

    filterOptions(input.value);
    updateClearButtonVisibility();
    updateSelectedValueTooltip();
  });

  input.addEventListener('blur', (e) => {
    setTimeout(() => {
      const nextFocused = e.relatedTarget || document.activeElement;
      if (nextFocused && (mainWrapper.contains(nextFocused) || listbox.contains(nextFocused))) return;
      closeList();
    }, 0);
  });

  listbox.addEventListener('mousedown', (e) => {
    const option = e.target.closest('[data-au-listbox-option]');
    if (!option || isOptionDisabled(option)) return;

    e.preventDefault();
    selectOption(option);
  });

  listbox.addEventListener('mouseover', (e) => {
    const option = e.target.closest('[data-au-listbox-option]');
    if (!option || isOptionDisabled(option)) return;

    const previousOption = e.relatedTarget?.closest?.('[data-au-listbox-option]');
    if (previousOption === option) return;

    setActiveOption(option);
    addOptionTooltip(option);

    const optionTooltip = option.querySelector(`[${OPTION_TOOLTIP_ATTR}]`);
    if (!optionTooltip) return;

    optionTooltip.style.left = `${e.clientX - optionTooltip.offsetWidth / 2}px`;
    optionTooltip.style.top = `${e.clientY - 56}px`;
    optionTooltip.style.visibility = 'visible';
    optionTooltip.style.opacity = 1;
  });

  input.addEventListener('keydown', (e) => {
    const isListOpen = !listbox.classList.contains(HIDDEN_CLASS);
    const visibleOptions = getVisibleOptions().filter((opt) => !isOptionDisabled(opt));

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (!isListOpen) {
          filterOptions(input.value);
          return;
        }
        const currentIndex = visibleOptions.indexOf(activeOption);
        const nextIndex = currentIndex + 1 >= visibleOptions.length ? 0 : currentIndex + 1;
        setActiveOption(visibleOptions[nextIndex]);
        break;
      }

      case 'ArrowUp': {
        e.preventDefault();
        if (!isListOpen) {
          filterOptions(input.value);
          return;
        }
        const currentIndex = visibleOptions.indexOf(activeOption);
        const prevIndex = currentIndex <= 0 ? visibleOptions.length - 1 : currentIndex - 1;
        setActiveOption(visibleOptions[prevIndex]);
        break;
      }

      case 'Enter':
        if (isListOpen && activeOption) {
          e.preventDefault();
          selectOption(activeOption);
        }
        break;

      case 'Escape':
        if (isListOpen) {
          e.preventDefault();
          closeList();
        }
        break;

      case 'Tab':
        closeList();
        break;

      default:
        break;
    }
  });

  if (isMobile) {
    input.addEventListener('touchstart', () => {
      filterOptions(input.value);
    });
  }

  input.addEventListener('mousemove', (e) => {
    const tooltip = mainWrapper.querySelector(`[${INPUT_TOOLTIP_ATTR}]`);
    if (!tooltip) return;

    tooltip.style.left = `${e.clientX - tooltip.offsetWidth / 2}px`;
    tooltip.style.top = `${e.clientY - 56}px`;
    tooltip.style.visibility = 'visible';
    tooltip.style.opacity = 1;
  });

  input.addEventListener('mouseleave', () => {
    const tooltip = mainWrapper.querySelector(`[${INPUT_TOOLTIP_ATTR}]`);
    if (!tooltip) return;

    tooltip.style.visibility = 'hidden';
    tooltip.style.opacity = 0;
  });

  if (clearButton) {
    clearButton.setAttribute('aria-controls', input.id);

    clearButton.addEventListener('mousedown', (e) => {
      isClearingFromButton = true;
      suppressFocusOpenOnce();
      e.preventDefault();
    });

    clearButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        isClearingFromButton = true;
        suppressFocusOpenOnce();
      }
    });

    clearButton.addEventListener('click', () => {
      closeList();
    });
  }

  // Estado inicial del botón clear (por si viene precargado con optionSelectedOnInit)
  updateClearButtonVisibility();
  updateSelectedValueTooltip();
}

function createLiveRegion() {
  let liveRegion = document.getElementById('au-predictive-live-region');

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'au-predictive-live-region';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'au-visually-hidden';
    document.body.appendChild(liveRegion);
  }

  return liveRegion;
}

function announceResults(count, liveRegion) {
  const message =
    count === 0
      ? 'No hay resultados'
      : `${count} resultado${count === 1 ? '' : 's'} disponible${count === 1 ? '' : 's'}`;

  liveRegion.textContent = message;
}

function addEventListeners() {
  // Observe DOM mutations to initialize predictive inputs that are inserted dynamically after first render.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.matches?.(SELECTOR)) {
            initInputs(node.parentElement || document);
          }

          if (node.querySelectorAll) {
            initInputs(node);
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

export default { init };
